import { Check, X, Phone, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Row = {
  category: string;
  feature: string;
  detail: string;
  us: boolean;
  them: boolean | "limited";
};
const ROWS: Row[] = [{
  category: "PRICING",
  feature: "Fixed-price quote before work starts",
  detail: "Written quote on-site. No surprises mid-job.",
  us: true,
  them: false
}, {
  category: "PRICING",
  feature: "Free callout & diagnosis",
  detail: "You only pay if you approve the quote.",
  us: true,
  them: "limited"
}, {
  category: "SPEED",
  feature: "Same-day service across Sydney",
  detail: "Average response time 22 minutes.",
  us: true,
  them: "limited"
}, {
  category: "SPEED",
  feature: "Parts stocked in van",
  detail: "Common springs, cables, capacitors on board — fixed first visit.",
  us: true,
  them: false
}, {
  category: "QUALITY",
  feature: "Licensed & insured technicians only",
  detail: "No apprentices doing the actual work. Fully insured trades.",
  us: true,
  them: "limited"
}, {
  category: "QUALITY",
  feature: "Roller-door specialists since 2009",
  detail: "Not a general handyman bolting this on — it's all we do.",
  us: true,
  them: false
}, {
  category: "WARRANTY",
  feature: "12-month workmanship warranty in writing",
  detail: "Free re-call if anything fails within the warranty.",
  us: true,
  them: "limited"
}, {
  category: "WARRANTY",
  feature: "Family-owned, locally based",
  detail: "Bass Hill HQ. ABN-registered. Real address, real people.",
  us: true,
  them: false
}];
export default function RollerComparison() {
  return <section style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      {/* Atmospheric gradients */}
      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      background: `radial-gradient(circle at 20% 30%, rgba(245,166,35,0.06) 0%, transparent 50%), radial-gradient(circle at 90% 80%, rgba(245,166,35,0.04) 0%, transparent 50%)`,
      pointerEvents: "none"
    }} />

      {/* Grain */}
      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      opacity: 0.03,
      mixBlendMode: "overlay",
      pointerEvents: "none",
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Watermark */}
      <div aria-hidden style={{
      position: "absolute",
      bottom: -40,
      right: -40,
      fontFamily: FONT_HEAD,
      fontSize: "clamp(160px, 20vw, 280px)",
      fontWeight: 900,
      color: "rgba(255,255,255,0.02)",
      letterSpacing: "-0.04em",
      lineHeight: 1,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 1
    }}>
        COMPARE
      </div>

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1200,
      margin: "0 auto"
    }}>
        {/* === HEADER === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 64,
        alignItems: "end"
      }} className="roller-comp-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20
          }}>
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD
            }}>
                06 / The honest comparison
              </span>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: "#ffffff",
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              How we stack up against the{" "}
              <span style={{
              color: GOLD,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                average Sydney tradie.
              </span>
            </h2>
          </div>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 17,
          color: "rgba(255,255,255,0.7)",
          lineHeight: 1.6,
          margin: 0,
          maxWidth: 460,
          justifySelf: "end"
        }}>
            We're not the cheapest in Sydney — we're the most thorough. Here's
            exactly what that means in practice.
          </p>
        </div>

        {/* === COMPARISON TABLE === */}
        <div style={{
        background: "rgba(255,255,255,0.025)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 4,
        overflow: "hidden"
      }}>
          {/* Table header */}
          <div style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.6fr) 140px 140px",
          padding: "20px 32px",
          background: NAVY_DEEP,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          gap: 24,
          alignItems: "center"
        }} className="roller-comp-thead">
            <div style={{
            fontFamily: FONT_BODY,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.5)"
          }}>
              What you should expect
            </div>
            <div style={{
            textAlign: "center",
            fontFamily: FONT_HEAD,
            fontSize: 14,
            fontWeight: 800,
            color: GOLD,
            letterSpacing: "0.05em",
            position: "relative"
          }}>
              <div style={{
              display: "inline-block",
              padding: "8px 16px",
              background: "rgba(245,166,35,0.1)",
              border: `1px solid ${GOLD}`,
              borderRadius: 100
            }}>
                GOLD GARAGE
              </div>
            </div>
            <div style={{
            textAlign: "center",
            fontFamily: FONT_BODY,
            fontSize: 13,
            fontWeight: 600,
            color: "rgba(255,255,255,0.4)",
            letterSpacing: "0.05em"
          }}>
              Average tradie
            </div>
          </div>

          {/* Rows */}
          {ROWS.map((row, i) => {
          const showCategoryHeader = i === 0 || ROWS[i - 1].category !== row.category;
          return <div key={i}>
                {showCategoryHeader && <div style={{
              padding: "20px 32px 8px",
              borderBottom: "1px solid rgba(255,255,255,0.04)",
              background: "rgba(255,255,255,0.015)"
            }}>
                    <span style={{
                fontFamily: FONT_BODY,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: GOLD
              }}>
                      {row.category}
                    </span>
                  </div>}

                <div style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.6fr) 140px 140px",
              padding: "24px 32px",
              borderBottom: i === ROWS.length - 1 ? "none" : "1px solid rgba(255,255,255,0.05)",
              gap: 24,
              alignItems: "center",
              transition: "background 0.2s"
            }} className="roller-comp-row" onMouseEnter={e => {
              e.currentTarget.style.background = "rgba(255,255,255,0.02)";
            }} onMouseLeave={e => {
              e.currentTarget.style.background = "transparent";
            }}>
                  <div>
                    <div style={{
                  fontFamily: FONT_HEAD,
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#ffffff",
                  lineHeight: 1.3,
                  letterSpacing: "-0.005em",
                  marginBottom: 6
                }}>
                      {row.feature}
                    </div>
                    <div style={{
                  fontFamily: FONT_BODY,
                  fontSize: 13,
                  color: "rgba(255,255,255,0.55)",
                  lineHeight: 1.5
                }}>
                      {row.detail}
                    </div>
                  </div>

                  {/* Us */}
                  <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}>
                    <CheckMark variant="us" />
                  </div>

                  {/* Them */}
                  <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}>
                    {row.them === true ? <CheckMark variant="them" /> : row.them === "limited" ? <LimitedMark /> : <XMark />}
                  </div>
                </div>
              </div>;
        })}

          {/* Legend */}
          <div style={{
          padding: "20px 32px",
          background: NAVY_DEEP,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
          fontFamily: FONT_BODY,
          fontSize: 12,
          color: "rgba(255,255,255,0.55)",
          alignItems: "center",
          justifyContent: "center"
        }}>
            <span style={{
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
              <CheckMark variant="us" small />
              Standard
            </span>
            <span style={{
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
              <LimitedMark small />
              Sometimes
            </span>
            <span style={{
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
              <XMark small />
              Not offered
            </span>
          </div>
        </div>

        {/* === BOTTOM CTA === */}
        <div style={{
        marginTop: 56,
        padding: "32px 40px",
        background: "rgba(245,166,35,0.06)",
        border: `1px solid ${GOLD}`,
        borderRadius: 4,
        display: "flex",
        flexWrap: "wrap",
        gap: 24,
        alignItems: "center",
        justifyContent: "space-between"
      }}>
          <div>
            <div style={{
            fontFamily: FONT_BODY,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: GOLD,
            marginBottom: 10
          }}>
              Already got a quote elsewhere?
            </div>
            <div style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(18px, 1.8vw, 22px)",
            fontWeight: 600,
            color: "#ffffff",
            lineHeight: 1.3,
            letterSpacing: "-0.005em"
          }}>
              Send us their quote — we'll match it or tell you honestly if
              it's a fair price.
            </div>
          </div>

          <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12
        }}>
            <a href="tel:0485000933" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "16px 24px",
            background: GOLD,
            color: NAVY,
            fontFamily: FONT_HEAD,
            fontSize: 15,
            fontWeight: 700,
            borderRadius: 8,
            textDecoration: "none",
            whiteSpace: "nowrap",
            transition: "transform 0.2s, box-shadow 0.2s",
            boxShadow: "0 6px 20px rgba(245,166,35,0.3)"
          }} onMouseEnter={e => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 10px 28px rgba(245,166,35,0.45)";
          }} onMouseLeave={e => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(245,166,35,0.3)";
          }}>
              <Phone size={16} strokeWidth={2.5} />
              Call 0485 000 933
            </a>
            <Link to="#contact-us" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "16px 24px",
            background: "transparent",
            color: "#ffffff",
            fontFamily: FONT_HEAD,
            fontSize: 15,
            fontWeight: 600,
            borderRadius: 8,
            textDecoration: "none",
            border: "1px solid rgba(255,255,255,0.3)",
            whiteSpace: "nowrap",
            transition: "background 0.2s, border-color 0.2s"
          }} onMouseEnter={e => {
            (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.08)";
            (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.5)";
          }} onMouseLeave={e => {
            (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
            (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.3)";
          }}>
              Send their quote
              <ArrowUpRight size={16} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          .roller-comp-header {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            align-items: start !important;
          }
          .roller-comp-thead,
          .roller-comp-row {
            grid-template-columns: minmax(0, 1fr) 70px 70px !important;
            padding-left: 20px !important;
            padding-right: 20px !important;
            gap: 12px !important;
          }
        }
        @media (max-width: 600px) {
          .roller-comp-thead {
            font-size: 10px !important;
          }
        }
      `}</style>
    </section>;
}

// === Mark components ===
function CheckMark({
  variant,
  small = false
}: {
  variant: "us" | "them";
  small?: boolean;
}) {
  const size = small ? 20 : 32;
  const iconSize = small ? 12 : 18;
  return <div style={{
    width: size,
    height: size,
    borderRadius: "50%",
    background: variant === "us" ? GOLD : "rgba(255,255,255,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: variant === "us" ? "0 4px 12px rgba(245,166,35,0.3)" : "none"
  }}>
      <Check size={iconSize} color={variant === "us" ? NAVY : "rgba(255,255,255,0.5)"} strokeWidth={3} />
    </div>;
}
function LimitedMark({
  small = false
}: {
  small?: boolean;
}) {
  const size = small ? 20 : 32;
  return <div style={{
    width: size,
    height: size,
    borderRadius: "50%",
    background: "transparent",
    border: "1px dashed rgba(255,255,255,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }}>
      <div style={{
      width: small ? 6 : 10,
      height: 2,
      background: "rgba(255,255,255,0.4)",
      borderRadius: 2
    }} />
    </div>;
}
function XMark({
  small = false
}: {
  small?: boolean;
}) {
  const size = small ? 20 : 32;
  const iconSize = small ? 12 : 16;
  return <div style={{
    width: size,
    height: size,
    borderRadius: "50%",
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }}>
      <X size={iconSize} color="rgba(255,255,255,0.35)" strokeWidth={2.5} />
    </div>;
}